<template>
  <section class="game-container flex justify-center">
    <div
      v-if="isLoaded"
      class="game-box"
      :class="{
        mobile: $device.mobile,
        desktop: !$device.mobile,
        horizontal: getSelectedDirection === 'horizontal',
        vertical: getSelectedDirection === 'vertical',
      }"
    >
      <img
        class="background left"
        src="@assets/img/allgame/background-left.png"
      />
      <img
        class="background right"
        src="@assets/img/allgame/background-right.png"
      />
      <iframe
        class="game-iframe"
        ref="gameFrame"
        scrolling="no"
        id="iframe"
        :src="`https://game.x-beta-pr.net/${getDirection()}/`"
        allow="fullscreen"
      ></iframe>
    </div>
  </section>
</template>

<script>
import { AuthActions, AuthMutations, AuthNamespace } from "@/store/auth";
import {
  MachineActions,
  MachineMutations,
  MachineNamespace,
} from "@/store/machine";
import { mapActions, mapGetters, mapMutations } from "vuex";

import machineMixin from "@/mixins/machineMixin";
import EventBus from "@/utils/EventBus";

export default {
  mixins: [machineMixin],
  props: {},
  data() {
    const params = new URL(document.location).searchParams;

    return {
      platFormType: false, // [ false = Desktop, true = Mobile ]
      machineId: params.get("id"),
      playerId: params.get("playerId"),
      playerToken: params.get("externalToken"),
      isLoaded: false,
    };
  },

  computed: {
    ...mapGetters(AuthNamespace, [
      "getExternalToken",
      "getPlayerId",
      "getUserCurrency",
      "getTokenExpiry",
    ]),

    ...mapGetters(MachineNamespace, ["getMachineId"]),
  },

  beforeDestroy() {
    // iframe message 리스너 제거
    window.removeEventListener("message", this.receiveMessage);
  },

  // 브라우저 뒤로가기 버튼 클릭시 호출 이벤트
  beforeRouteLeave(to, from, next) {
    next(false);
    return "";
  },

  async mounted() {
    EventBus.$on("movePageEvent", this.movePageEvent);

    // iframe message 리스너 추가
    window.addEventListener("message", this.receiveMessage);

    // 브라우저 크기 변경 시 호출
    this.updateHeight();
    window.addEventListener("resize", this.updateHeight);

    //게임을 로드하는 부분
    await this.createMachinePlayer(this.machineId, {
      isRedirect: false,
    });
    this.isLoaded = true;
  },

  methods: {
    ...mapActions(AuthNamespace, {
      ACTION_AUTH_LOGOUT: AuthActions.ACTION_AUTH_LOGOUT,
    }),

    ...mapMutations(AuthNamespace, {
      MUTATION_UPDATE_AFK_INFO: AuthMutations.MUTATION_UPDATE_AFK_INFO,
    }),

    ...mapActions(MachineNamespace, {
      ACTION_CREATE_MACHINE_PLAYER: MachineActions.ACTION_CREATE_MACHINE_PLAYER,
      ACTION_DELETE_MACHINE_PLAYER: MachineActions.ACTION_DELETE_MACHINE_PLAYER,
    }),

    ...mapMutations(MachineNamespace, {
      MUTATION_MACHINE_KICK_OFF: MachineMutations.MUTATION_MACHINE_KICK_OFF,
      MUTATION_USER_MACHINE: MachineMutations.MUTATION_USER_MACHINE,
    }),

    getDirection() {
      let direction = "horizontal";
      if (this.$device.mobile) direction = this.getSelectedDirection;

      return direction === "vertical" ? "mobile" : "desktop";
    },

    // 게임 클라이언트로부터 전달받은 준비완료 메시지
    receiveMessage(e) {
      this.player_ban = false;

      switch (e.data.functionName) {
        case "ReceiveGameState":
          e.source.postMessage(
            {
              functionName: "ReceiveGameState",
              data: {
                token: this.getExternalToken,
                machine_id: this.getMachineId,
                currency: this.getUserCurrency,
              },
            },
            "*"
          );
          break;

        case "MoveToMain":
          this.closeGame();
          break;

        case "KickToMain":
          this.MUTATION_MACHINE_KICK_OFF(true);
          this.closeGame();
          break;

        // AFK Kicked Out
        case "KickToMainAfk":
          this.MUTATION_UPDATE_AFK_INFO(
            e.data.params && e.data.params.length > 0 ? e.data.params[0] : 0
          );
          this.closeGame();
          break;

        case "BanToMain":
          this.player_ban = true;
          this.closeGame();
          break;

        case "requestFullScreen":
          // 전체화면 설정
          this.fullScreen = true;
          this.changeFullSize();
          break;

        case "exitFullScreen":
          // 전체화면 해제
          this.fullScreen = false;
          this.changeFullSize();
          break;
        default:
          break;
      }
    },

    closeGame() {
      const machine_id = this.machineId;
      const player_id = this.playerId;
      const external_token = this.playerToken;

      // 선택한 머신에 사용자 정보 삭제
      this.ACTION_DELETE_MACHINE_PLAYER({
        machine_id,
        player_id,
        external_token,
      }).then(() => {
        // returnUrl 파라미터를 추출하여 이동, 없으면 창 닫기
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get("returnUrl");
        if (returnUrl) {
          window.location.href = returnUrl;
        } else {
          top.window.close();
        }
      });
    },

    getActualViewportHeight() {
      if (window.visualViewport) {
        return window.visualViewport.height;
      }
      return window.innerHeight;
    },

    updateHeight() {
      const actualHeight = Math.floor10(
        this.getActualViewportHeight() / 100,
        1
      );
      const actualWidth = Math.floor10(window.innerWidth / 100, 1);
      document.documentElement.style.setProperty(
        "--actual-vh",
        `${actualHeight}px`
      );
      document.documentElement.style.setProperty(
        "--actual-vw",
        `${actualWidth}px`
      );
      document.documentElement.style.height = `calc(var(--actual-vh, 1vh) * 100)`;
      document.documentElement.style.maxHeight = `calc(var(--actual-vh, 1vh) * 100)`;
    },

    // iframe postMessage 전달
    sendPostMessage() {
      this.$refs.gameFrame.contentWindow.postMessage(
        { functionName: "OpenCreditOutPopup" },
        "*"
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/import.scss";

.game-container {
  position: relative;
  width: 100%;
  height: calc(var(--actual-vh, 1vh) * 100);
  background-color: black;
  overflow: hidden;

  img.background {
    position: absolute;
    height: 100%;
    top: 0;
  }
  img.background.left {
    right: 100%;
  }
  img.background.right {
    left: 100%;
  }

  .game-box {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: contain;

    //PC환경인경우 contain으로 동작되도록 작성
    @include device-desktop() {
      max-width: 100vh * 1.77;
      height: 100vw * 0.56;
      max-height: 100vh;
    }

    .game-iframe {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    //모바일에서 세로가 더 긴 환경인경우
    @include device-lte-tablet() {
      @media (min-height: 70vw) {
        &.horizontal {
          width: calc(1vw * 100);
          height: calc(1vh * 100);
          max-width: calc(1vh * 720 / 1280 * 100);
          max-height: calc(1vw * 1280 / 720 * 100);
          .game-iframe {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%) rotate(90deg) scale(0.5);
            width: calc(var(--actual-vh, 1vh) * 100 * 2);
            height: calc(var(--actual-vw, 1vw) * 100 * 2);
            max-width: calc(var(--actual-vw, 1vw) * 1280 / 720 * 100 * 2);
            max-height: calc(var(--actual-vh, 1vh) * 720 / 1280 * 100 * 2);
          }
        }
      }
    }
  }
}
</style>
